// src/sim/foxguard/shout.ts — P3 RNG-picked caught-hider shouter (GAME-14, D-17).
//
// Invariants:
//   - Pure: reads world + rng; returns selected Entity or null; never mutates.
//   - Deterministic given (world, mapData, currentTick, same rng state).
//   - Uses SimRng.foxguard sub-seed exclusively (Pitfall 1 + Pitfall 10).
//   - Shallow reference return — caller is responsible for applying
//     `lastFoxguardShoutTick = currentTick` update on the picked hider.
//   - Candidate pool deterministically sorted by id before the RNG draw so
//     seed equality produces stable picks across runs.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-17.

import type { Entity, WorldState } from '../../shared/types/world';
import type { MapData } from '../../shared/types/map';
import { zoneAt } from '../map/zones';
// P7 POL-06 / D-12: cooldown read through the tuning cache (./constants is the fallback default).
import { getTuning } from '../content/cache';

/**
 * D-17 caught-hider shouter selection.
 *
 * Candidate pool = hiders with status='caught', escortedBy=null (parked at
 * base, not mid-escort), and position inside the plinth polygon. From that
 * pool, filter out any hider within the 15 s cooldown window. If none remain,
 * return null (caller decides whether to still reset the streak).
 *
 * Returns the selected entity as a shallow reference from `world.entities` —
 * the caller is expected to produce the entity update (setting
 * `lastFoxguardShoutTick = currentTick`) and emit the `foxguard-called` event.
 */
export function pickShouter(
  world: WorldState,
  mapData: MapData,
  currentTick: number,
  rngFoxguard: () => number,
): Entity | null {
  const caughtAtBase = Object.values(world.entities)
    .filter(
      (e) =>
        e.role === 'hider' &&
        e.status === 'caught' &&
        e.escortedBy === null &&
        zoneAt(e.position, mapData.zones) === 'plinth',
    )
    .sort((a, b) => a.id.localeCompare(b.id));

  const cooldownTicks = getTuning().foxguardShoutCooldownTicks;
  const eligible = caughtAtBase.filter(
    (e) =>
      e.lastFoxguardShoutTick === null ||
      currentTick - e.lastFoxguardShoutTick >= cooldownTicks,
  );
  if (eligible.length === 0) return null;

  const idx = Math.floor(rngFoxguard() * eligible.length);
  // Defensive clamp — alea returns [0, 1) but guard the upper bound in case of
  // future PRNG swaps returning 1.0 at edge cases.
  return eligible[Math.min(idx, eligible.length - 1)]!;
}
